import React, { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
    SimpleForm,
    Edit,
    TextInput,
    BooleanInput,
    SaveButton,
    DeleteWithConfirmButton,
    Toolbar,
    Notification,
    useNotify,
    NumberInput,
    ArrayInput,
    FormDataConsumer
} from "react-admin";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import { ThemeProvider } from "@material-ui/styles";
import { useForm } from "react-final-form";
import axios from "axios";
import customTheme from "../../customTheme";
import { apiUrl } from "../../dataProvider";
import { validateMagicNUmber, validateRequired, valueBetween } from "../../utils/Validations";
import WithProps from "../Custom/WithProps";
import CustomAdminSelect from "./CustomAdminSelect";
import VideoPreview from "./VideoPreview";
import { CustomArrayIterator } from "./create-bestseller";

const useStyles = makeStyles(() => ({
    root: {
        width: "90%",
        margin: "auto",
    },
    toolbar: {
        justifyContent: "space-between",
    },
    grouping: {
        border: "1px solid lightgray",
        borderRadius: "3px",
        margin: "1% auto",
    },
    warnLabel: {
        fontSize: "10px",
        color: "red",
        display: "block",
    },
    displayFlex: {
        display: "flex",
    },
    equalWidth: {
        maxWidth: "33%",
    },
    heading: {
        fontWeight: "bold",
        marginTop: "10px",
        borderBottom: "3px solid #a6a5a5ff",
        paddingBottom: "5px",
    },
}));

const EditToolbar = (props) => {
    const classes = useStyles();
    return (
        <Toolbar {...props} className={classes.toolbar}>
            <SaveButton disabled={props.pristine} />
            <DeleteWithConfirmButton />
        </Toolbar>
    );
};

EditToolbar.propTypes = {
    pristine: PropTypes.bool,
};

const BuilderPlatformDataSync = ({ bestsellerId, builderPlatformId }) => {
    const form = useForm();
    const notify = useNotify();
    const previousBuilderPlatformIdRef = useRef(builderPlatformId);

    useEffect(() => {
        const previousBuilderPlatformId = previousBuilderPlatformIdRef.current;
        previousBuilderPlatformIdRef.current = builderPlatformId;

        if (builderPlatformId === previousBuilderPlatformId) {
            return;
        }

        if (!builderPlatformId) {
            form.change("builder_platform_data", {});
            return;
        }

        const fetchBuilderPlatformData = async () => {
            try {
                const response = await axios.get(
                    `${apiUrl}/bestsellers/${bestsellerId}?builder_platform_id=${builderPlatformId}`
                );

                const builderPlatformData = response?.data?.data?.builder_platform_data;
                if (builderPlatformData) {
                    form.change("builder_platform_data", builderPlatformData);
                } else {
                    form.change("builder_platform_data", {
                        builder_platform_id: builderPlatformId,
                        discount_percentage: null,
                        similar_rings: [],
                    });
                }
            } catch (error) {
                notify(
                    error?.response?.data?.message || "Failed to fetch builder platform data",
                    { type: "warning" }
                );
            }
        };

        fetchBuilderPlatformData();
    }, [builderPlatformId, bestsellerId, form, notify]);

    return null;
};

BuilderPlatformDataSync.propTypes = {
    bestsellerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    builderPlatformId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const EditBestSeller = (props) => {
    const classes = useStyles();

    const [checked, setChecked] = useState();
    const [kaysChecked, setKaysChecked] = useState();

    const handleChange = useCallback(
        (newChecked) => setChecked(newChecked),
        []
    );

    const handleKaysChange = useCallback(
        (newChecked) => setKaysChecked(newChecked),
        []
    );

    const notify = useNotify();
    const onFailure = (error) => {
        notify(
            error.response.data.message
                ? error.response.data.message
                : "Something went wrong",
            { type: "warning" }
        );
    };

    return (
        <ThemeProvider theme={customTheme}>
            <Edit mutationMode="pessimistic" onFailure={onFailure} {...props}>
                <WithProps>
                    {({ record, ...props }) => (
                        <SimpleForm
                            record={record}
                            {...props}
                            redirect="list"
                            toolbar={<EditToolbar />}
                        >
                            <Grid
                                container
                                spacing={2}
                                className={classes.root}
                                justify="space-between"
                            >
                                <Grid
                                    item
                                    container
                                    xs={12}
                                    className={classes.displayFlex}
                                    justify="space-between"
                                    alignItems="center"
                                >
                                    <Grid item xs={6}>
                                        <TextInput
                                            source="magic_number"
                                            label="Magic Number"
                                            validate={validateRequired}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <NumberInput
                                            source="discount_percentage"
                                            label="Discount Percentage"
                                            validate={[
                                                validateRequired,
                                                valueBetween(0, 100),
                                            ]}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <BooleanInput
                                            label={
                                                (typeof checked ===
                                                    "undefined" &&
                                                    record.active) ||
                                                checked
                                                    ? "Active"
                                                    : "Inactive"
                                            }
                                            source="active"
                                            initialValue={true}
                                            onChange={handleChange}
                                            checked={checked}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <CustomAdminSelect
                                            label="Video URL"
                                            source="video_url"
                                            resource="getBestsellerVideoList"
                                            validate={validateRequired}
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <BooleanInput
                                            label="Hide Quarter Sizes"
                                            source="hide_quarter_sizes"
                                            initialValue={false}
                                        />
                                    </Grid>
                                </Grid>
                                <VideoPreview />
                                <Grid item xs={5}>
                                    {[1, 2, 3, 4, 5].map((ringPosition) => (
                                        <TextInput
                                            key={`similar-ring-${ringPosition}`}
                                            source={`similar_rings[${ringPosition - 1}]`}
                                            label={`Similar Ring ${ringPosition}`}
                                            validate={validateMagicNUmber}
                                            fullWidth
                                        />
                                    ))}
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                spacing={2}
                                className={classes.root}
                                alignItems="center"
                                justify="space-between"
                            >
                                <Grid
                                    item
                                    container
                                    xs={12}
                                    className={classes.heading}
                                >
                                    Kay's Configuration
                                </Grid>
                                <Grid item xs={3}>
                                    <NumberInput
                                        source="kays_discount_percentage"
                                        label="Kay's Discount Percentage"
                                        validate={[
                                            validateRequired,
                                            valueBetween(0, 100),
                                        ]}
                                        fullWidth
                                    />
                                </Grid>
                                {/* <Grid item xs={5}>
                                    <ArrayInput source="magic_numbers_for_kays_images">
                                        <CustomArrayIterator
                                            arraySource="magic_numbers_for_kays_images"
                                            fieldLabel="Magic Number"
                                            maxItems={4}
                                        />
                                    </ArrayInput>
                                </Grid> */}
                                <Grid item xs={3}>
                                    <BooleanInput
                                        label={
                                            (typeof kaysChecked ===
                                                "undefined" &&
                                                record.active_for_kays) ||
                                            kaysChecked
                                                ? "Active For Kays"
                                                : "Inactive For Kays"
                                        }
                                        source="active_for_kays"
                                        initialValue={true}
                                        onChange={handleKaysChange}
                                        checked={kaysChecked}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                spacing={2}
                                className={classes.root}
                                alignItems="center"
                                justify="space-between"
                            >
                                <Grid
                                    item
                                    container
                                    xs={12}
                                    className={classes.heading}
                                >
                                    Builder Platform Configuration
                                </Grid>
                                <Grid item xs={3}>
                                    <CustomAdminSelect
                                        label="Builder Platform"
                                        source="builder_platform_data.builder_platform_id"
                                        resource="builder-platform?page[number]=1&page[size]=1000"
                                        optionLabel="store_name"
                                    />
                                </Grid>
                                <FormDataConsumer>
                                    {({ formData }) => {
                                        const builderPlatformId = formData?.builder_platform_data?.builder_platform_id;
                                        const showBuilderFields =
                                            builderPlatformId !== undefined &&
                                            builderPlatformId !== null &&
                                            builderPlatformId !== "";

                                        return (
                                            <>
                                                <BuilderPlatformDataSync
                                                    bestsellerId={record?.id}
                                                    builderPlatformId={builderPlatformId}
                                                />
                                                {showBuilderFields ? (
                                                    <>
                                                <Grid item xs={3}>
                                                    <NumberInput
                                                        source="builder_platform_data.discount_percentage"
                                                        label="Builder Discount Percentage"
                                                        validate={[
                                                            validateRequired,
                                                            valueBetween(0, 100),
                                                        ]}
                                                        fullWidth
                                                    />
                                                </Grid>
                                                <Grid item xs={5}>
                                                    {[1, 2, 3, 4, 5].map((ringPosition) => (
                                                        <TextInput
                                                            key={`builder-ring-${ringPosition}`}
                                                            source={`builder_platform_data.similar_rings[${ringPosition - 1}]`}
                                                            label={`Similar Ring ${ringPosition}`}
                                                            fullWidth
                                                            validate={validateMagicNUmber}
                                                        />
                                                    ))}
                                                </Grid>
                                                    </>
                                                ) : null}
                                            </>
                                        );
                                    }}
                                </FormDataConsumer>
                            </Grid>
                        </SimpleForm>
                    )}
                </WithProps>
            </Edit>
            <Notification />
        </ThemeProvider>
    );
};

export default EditBestSeller;
